import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { SidebarProvider, useSidebar } from '../../components/ui/Sidebar';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import CompletionOverview from './components/CompletionOverview';
import ResultsTable from './components/ResultsTable';
import CompletionActions from './components/CompletionActions';
import MetricsCards from './components/MetricsCards';
import Icon from '../../components/AppIcon';

const ProcessingCompletionSummaryContent = () => {
  const navigate = useNavigate();
  const { isCollapsed } = useSidebar();
  const { t, i18n } = useTranslation('pages');
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);

  // Mock batch completion data
  const batchData = {
    batchId: "BATCH-2025-001-789",
    totalInvoices: 45,
    successfulInvoices: 38,
    failedInvoices: 4,
    warningInvoices: 3,
    processingTime: "12m 34s",
    zatcaComplianceRate: 84.4,
    completedAt: new Date()?.toISOString(),
    startedAt: new Date(Date.now() - 754000)?.toISOString()
  };

  // Mock individual invoice results
  const invoiceResults = [
    {
      id: "inv-001",
      filename: "INV-2025-001.pdf",
      status: "success",
      stepsCompleted: 5,
      errorCount: 0,
      processingTime: 15.2,
      errors: []
    },
    {
      id: "inv-002",
      filename: "INV-2025-002.pdf",
      status: "failed",
      stepsCompleted: 3,
      errorCount: 2,
      processingTime: 8.7,
      errors: [
        {
          step: "VAT Validation",
          message: "Invalid VAT number format detected in line item 3",
          recommendation: "Verify VAT number follows Saudi Arabia format (15 digits)"
        },
        {
          step: "ZATCA Clearance",
          message: "Document signature verification failed",
          recommendation: "Re-upload with valid digital signature"
        }
      ]
    },
    {
      id: "inv-003",
      filename: "INV-2025-003.pdf",
      status: "warning",
      stepsCompleted: 5,
      errorCount: 1,
      processingTime: 18.9,
      errors: [
        {
          step: "AI Anomaly Detection",
          message: "Unusual amount pattern detected - significantly higher than historical average",
          recommendation: "Review invoice amount for accuracy"
        }
      ]
    },
    {
      id: "inv-004",
      filename: "INV-2025-004.pdf",
      status: "success",
      stepsCompleted: 5,
      errorCount: 0,
      processingTime: 12.1,
      errors: []
    },
    {
      id: "inv-005",
      filename: "INV-2025-005.pdf",
      status: "failed",
      stepsCompleted: 2,
      errorCount: 3,
      processingTime: 6.3,
      errors: [
        {
          step: "File Conversion",
          message: "PDF structure corrupted - unable to extract text content",
          recommendation: "Re-scan document or use higher quality PDF"
        },
        {
          step: "Schema Validation",
          message: "Required field 'supplier_address' missing from document",
          recommendation: "Ensure all mandatory fields are present"
        },
        {
          step: "CRN Validation",
          message: "Commercial Registration Number not found",
          recommendation: "Add valid CRN to invoice header"
        }
      ]
    }
  ];

  // Mock metrics data
  const metricsData = {
    totalProcessingTime: 754,
    averageProcessingTime: 16.8,
    zatcaComplianceRate: 84.4,
    errorCategories: [
      {
        type: "VAT Validation Errors",
        description: "Invalid VAT number formats or calculations",
        count: 8,
        severity: "high"
      },
      {
        type: "Document Structure Issues",
        description: "PDF parsing or format problems",
        count: 5,
        severity: "medium"
      },
      {
        type: "Missing Required Fields",
        description: "Mandatory invoice fields not found",
        count: 3,
        severity: "high"
      },
      {
        type: "AI Anomaly Warnings",
        description: "Unusual patterns detected by AI analysis",
        count: 2,
        severity: "low"
      }
    ],
    peakProcessingHour: "14:00 - 15:00",
    systemPerformance: {
      score: 87,
      status: "Good Performance",
      cpuUsage: 68,
      memoryUsage: 72,
      throughput: 3.2
    }
  };

  useEffect(() => {
    // Simulate loading delay
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  const handleNavigation = (path) => {
    navigate(path);
  };

  const tabs = [
    { id: 'overview', label: t('completion.overview'), icon: 'BarChart3' },
    { id: 'results', label: t('completion.detailedResults'), icon: 'FileText' },
    { id: 'metrics', label: t('completion.analytics'), icon: 'TrendingUp' },
    { id: 'actions', label: t('completion.actions'), icon: 'Settings' }
  ];
  const locale = i18n.language === 'ar' ? 'ar-SA' : 'en-GB';

  if (isLoading) {
    return (
      <div 
        className={`min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 transition-all duration-300 ease-in-out ${
          isCollapsed ? 'md:ms-16' : 'md:ms-64'
        }`}
      >
        <Helmet>
          <title>{t('completion.title')} - ZATCA Compliance Management</title>
          <meta
            name="description"
            content={t('completion.description')}
          />
        </Helmet>

        <Header 
          title={t('completion.title')}
          description={t('completion.description')}
        />
        
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="text-center space-y-6">
            {/* Fixed Loader */}
            <div className="flex justify-center">
              <div className="relative">
                {/* Outer spinning ring */}
                <div className="w-16 h-16 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
                {/* Inner static circle */}
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                  <div className="w-3 h-3 bg-primary rounded-full animate-pulse"></div>
                </div>
              </div>
            </div>
            
            <div className="space-y-3">
              <h2 className="text-xl font-semibold text-foreground">
                {t('completion.generatedTitle')}
              </h2>
              <p className="text-muted-foreground max-w-md">
                {t('completion.generatedDescription')}
              </p>
              
              {/* Progress dots */}
              <div className="flex justify-center gap-2 pt-4">
                <div className="w-2 h-2 bg-primary rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 transition-all duration-300 ease-in-out ${
        isCollapsed ? 'md:ms-16' : 'md:ms-64'
      }`}
    >
      <Helmet>
        <title>{t('completion.title')} - ZATCA Compliance Management</title>
        <meta
          name="description"
          content={t('completion.description')}
        />
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        {/* Header Component */}
        <Header 
          title={t('completion.title')}
          description={t('completion.description')}
        />

        <main className="space-y-6">
          {/* Status Header Section */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-hero-cta border border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="flex items-center justify-center w-12 h-12 bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg">
                  <Icon name="CheckCircle" size={24} className="text-green-600 dark:text-green-400" strokeWidth={2} />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">
                    {t('completion.successTitle')}
                  </h1>
                  <p className="text-muted-foreground">
                    {t('completion.batchFinished')} • {new Date()?.toLocaleDateString(locale)} {new Date()?.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="border-t border-gray-100 dark:border-gray-700 pt-6">
              <div className="flex overflow-x-auto gap-1">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`
                      flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap
                      transition-all duration-200 ease-out hover:scale-[1.02]
                      ${activeTab === tab?.id
                        ? 'bg-primary text-primary-foreground shadow-md ring-1 ring-primary/20'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/70'
                      }
                    `}
                  >
                    <Icon name={tab?.icon} size={16} color="currentColor" strokeWidth={2} />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <CompletionOverview batchData={batchData} />
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2">
                    <ResultsTable invoiceResults={invoiceResults?.slice(0, 3)} />
                  </div>
                  <div>
                    <CompletionActions batchData={batchData} onNavigate={handleNavigation} />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'results' && (
              <ResultsTable invoiceResults={invoiceResults} />
            )}

            {activeTab === 'metrics' && (
              <MetricsCards metricsData={metricsData} />
            )}

            {activeTab === 'actions' && (
              <CompletionActions batchData={batchData} onNavigate={handleNavigation} />
            )}
          </div>

          {/* Success Message */}
          {batchData?.successfulInvoices > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-hero-cta border border-green-200 dark:border-green-700">
              <div className="flex items-center gap-3">
                <Icon name="CheckCircle" size={24} className="text-green-600 dark:text-green-400" strokeWidth={2} />
                <div>
                  <h3 className="font-semibold text-green-600 dark:text-green-400 mb-1">
                    {t('completion.successTitle')}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {t('completion.successMessage', {
                      successful: batchData?.successfulInvoices,
                      total: batchData?.totalInvoices,
                    })}
                    {batchData?.failedInvoices > 0 &&
                      ` ${t('completion.requiresAttention', {
                        count: batchData?.failedInvoices,
                      })}`}
                  </p>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

const ProcessingCompletionSummary = () => {
  return (
    <SidebarProvider>
      <div className="min-h-screen bg-background">
        <Sidebar />
        <ProcessingCompletionSummaryContent />
      </div>
    </SidebarProvider>
  );
};

export default ProcessingCompletionSummary;
